import { useState } from "react";
import { AlertTriangle, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";

const money = (v) => (v ? `$${Number(v).toFixed(2)}` : "—");

function PreviewRow({ ln, i, types, onChange }) {
  const rates = ln.kind === "slider"
    ? (ln.tracks || []).map((t) => `${t.name} ${money(t.rate)}`).join(" · ")
    : `${money(ln.frame_rate)} · ${money(ln.sash_std_rate)} / ${money(ln.sash_over_rate)}`;
  return (
    <tr data-testid={`import-row-${i}`} className="border-b border-slate-800/70 align-top">
      <td className="px-2 py-2"><input type="checkbox" data-testid={`import-include-${i}`} checked={ln.include} onChange={(e) => onChange({ include: e.target.checked })} /></td>
      <td className="px-2 py-2 text-slate-100">{ln.name}{ln.exists && <span className="ml-2 rounded bg-sky-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">updates existing</span>}
        <span data-testid={`import-category-${i}`} className="block text-[11px] text-slate-500">{ln.category_label || "No category"}</span></td>
      <td className="px-2 py-2 font-mono-num text-xs text-slate-300">{rates}</td>
      <td className="px-2 py-2 text-right font-mono-num text-xs text-slate-300">{ln.kind === "slider" || !ln.sash_std_rate ? "—" : Number(ln.std_sash_area ?? 13.73).toFixed(2)}</td>
      <td className="px-2 py-2 text-right font-mono-num text-xs text-slate-300">{Number(ln.min_area || 0).toFixed(2)}{ln.min_area_double ? ` / ${Number(ln.min_area_double).toFixed(2)} dbl` : ""}{ln.valid_max ? ` · range ${Number(ln.valid_min).toFixed(2)}–${Number(ln.valid_max).toFixed(2)}` : ""}</td>
      <td className="px-2 py-2 text-right font-mono-num text-xs text-slate-300">{money(ln.crate_rate)}</td>
      <td className="px-2 py-2 text-xs text-slate-400">{(ln.glass_tiers || []).length} tiers · {(ln.extras || []).length} extras</td>
      <td className="px-2 py-2">
        {ln.category === "screen" ? (
          <span data-testid={`import-type-${i}`} className="text-xs font-semibold text-emerald-300">Screen add-on</span>
        ) : (
        <select data-testid={`import-type-${i}`} value={ln.type_id || ""} onChange={(e) => onChange({ type_id: e.target.value || null })} className={`${inputCls} min-w-[170px]`}>
          <option value="">Not mapped</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        )}
      </td>
    </tr>
  );
}

export function SupplierImportDialog({ types, onClose, onDone }) {
  const [res, setRes] = useState(null);
  const [lines, setLines] = useState([]);
  const [useForTypes, setUseForTypes] = useState(true);
  const [busy, setBusy] = useState(false);
  const read = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/product-lines/import/preview", fd);
      setRes(data);
      setLines(data.lines.map((l) => ({ ...l, include: true })));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/product-lines/import", { lines: lines.filter((l) => l.include), use_component_for_types: useForTypes });
      toast.success(`Imported ${data.saved} product lines${data.types_switched ? `, ${data.types_switched} types now use Component pricing` : ""}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const patch = (i, p) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...p } : l)));
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="supplier-import-dialog" className="max-h-[92vh] max-w-6xl overflow-y-auto bg-slate-950">
        <DialogHeader>
          <DialogTitle className="font-display text-slate-50">Import supplier price list</DialogTitle>
          <DialogDescription className="text-slate-400">Reads frame, sash, crate, glass and extras from the supplier .xlsx. All rates are converted to $/sq ft. Nothing is saved until you confirm.</DialogDescription>
        </DialogHeader>
        {!res ? (
          <label data-testid="supplier-import-drop" className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed border-slate-700 p-10 text-sm text-slate-300 hover:border-amber-500/60">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <FileSpreadsheet className="h-6 w-6 text-amber-300" />}
            Choose the supplier .xlsx
            <input data-testid="supplier-import-file-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => read(e.target.files[0])} />
          </label>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="supplier-import-preview">
                <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                  <tr><th /><th className="px-2">Product line</th><th className="px-2">Frame · sash std / oversized ($/sq ft)</th><th className="whitespace-nowrap px-2 text-right">Std sash</th>
                    <th className="whitespace-nowrap px-2 text-right">Min area</th><th className="px-2 text-right">Crate</th><th className="px-2">Glass/extras</th><th className="px-2">type</th></tr>
                </thead>
                <tbody>{lines.map((ln, i) => <PreviewRow key={ln.name} ln={ln} i={i} types={types} onChange={(p) => patch(i, p)} />)}</tbody>
              </table>
            </div>
            {res.manual?.length > 0 && (
              <div data-testid="supplier-import-manual" className="rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-200"><AlertTriangle className="h-4 w-4" /> Needs your input after import</div>
                <ul className="list-disc space-y-1 pl-5 text-xs text-slate-300">{res.manual.map((m, i) => <li key={i} data-testid={`supplier-import-manual-${i}`}><span className="text-slate-100">{m.item}</span> — {m.why}</li>)}</ul>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" data-testid="supplier-import-use-for-types" checked={useForTypes} onChange={(e) => setUseForTypes(e.target.checked)} />
              Switch mapped types to Component pricing (saved quotes are not changed)
            </label>
          </>
        )}
        <DialogFooter>
          <button data-testid="supplier-import-cancel" onClick={onClose} disabled={busy} className="rounded-full px-4 py-2 text-sm text-slate-300 hover:text-slate-50">Cancel</button>
          {res && (
            <button data-testid="supplier-import-confirm" onClick={confirm} disabled={busy || !lines.some((l) => l.include)}
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-40">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Import {lines.filter((l) => l.include).length} lines
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
